import PropTypes from 'prop-types';
import '../../../assets/styles/components/ResultBar.css';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import { MISSING_TRANSLATION } from '../../../utils/helpers/translation';
import { getTempEdgeResponseCode, getTempEdgeResponseStatus } from '../../../utils/tempEdgeResponse';

/**
 * Resolve the bar style from the response. TE-E08 is a warning regardless of
 * status; TE00 is success when status is missing or < 300 (BE2 often omits status).
 * @author Armando Rico -ai-
 */
export const resolveOutcomeType = (status, code) => {
  const numericStatus = Number(status);
  const isSuccessStatus = Number.isNaN(numericStatus) || numericStatus < 300;

  if (code === 'TE-E08') return 'warning';
  if (code === 'TE00' && isSuccessStatus) return 'success';
  return 'fail';
};

const isTranslationKey = (value) => typeof value === 'string' && value.startsWith('com.tempedge.');

const getRawBackendMessage = (response) => {
  const raw = response?.message ?? response?.data?.message;
  const isBackendText = typeof raw === 'string' && raw.trim() !== '' && !isTranslationKey(raw) && !raw.includes('Request');
  return isBackendText ? raw : '';
};

/**
 * Text the bar shows. A translated message always wins. When it is missing the bar normally
 * falls back to the generic error; with `showRawMessage` it first tries the translation of the
 * backend `errorKey` and then the backend's own plain text, so the bar says what the server
 * answered. A dictionary key that failed to translate is never shown raw.
 * @param {Object} params - Resolution inputs.
 * @param {string} params.translated - Result of translating the response message.
 * @param {*} params.response - Response or error payload handed to the bar.
 * @param {boolean} params.showRawMessage - Whether the backend text may be shown untranslated.
 * @param {Function} params.translate - Translator, called as translate(key, data).
 * @param {*} params.customData - Interpolation data for the translator.
 * @returns {string} The text to render in the bar.
 * @author armandoricosandoval -ai-
 */
export const resolveOutcomeMessage = ({ translated, response, showRawMessage, translate, customData }) => {
  if (translated && translated !== MISSING_TRANSLATION) return translated;

  if (showRawMessage) {
    const errorKeyText = response?.errorKey ? translate(response.errorKey, customData) : '';
    if (errorKeyText && errorKeyText !== MISSING_TRANSLATION) return errorKeyText;

    const rawMessage = getRawBackendMessage(response);
    if (rawMessage) return rawMessage;
  }

  return translate('com.tempedge.error.undefine', customData);
};

const OutcomeBar = (props) => {
  const { customData = '', response = '', customMessage = '', personMessage = '', showRawMessage = false } = props;
  const { tempedgeTranslator } = useTempEdgeTranslator();
  let message = '';

  if (
    (response?.message && response?.message.includes('Request')) ||
    (response?.data?.message && response?.data?.message.includes('Request'))
  ) {
    message = 'com.tempedge.error.undefine';
  } else {
    message = response?.message && response?.message !== '' ? response?.message : response?.data?.message;
  }

  const code = getTempEdgeResponseCode(response);
  const status = getTempEdgeResponseStatus(response);
  const type = resolveOutcomeType(status, code);

  const translated = personMessage !== '' ? tempedgeTranslator(personMessage) : tempedgeTranslator(message, customData);
  const barMessage = resolveOutcomeMessage({ translated, response, showRawMessage, translate: tempedgeTranslator, customData });

  return (
    <div className={`announcement-bar ${type}`}>
      <p>
        {code && response?.showCode !== false && `${code} - `}
        <span>{barMessage}</span>
        {personMessage === '' && customMessage !== '' && customMessage !== null ? ' - ' + customMessage : ''}
      </p>
    </div>
  );
};

OutcomeBar.propTypes = {
  showRawMessage: PropTypes.bool,
  customData: PropTypes.shape({
    data: PropTypes.oneOfType([PropTypes.string, PropTypes.string])
  })
};

OutcomeBar.defaultProps = {
  customData: { data: '' }
};

export default OutcomeBar;
